import CopyToClipboard from '@src/components/CopyToClipboard'
import FuelIcon from '@src/components/icons/Fuel'
import { LiquiditySource } from '@src/enums'
import { ChainSpecificGasDetails } from '@src/pages/Exchange/components/GasFeePanel/ChainSpecificGasDetails'
import { JupiterRouteDetails } from '@src/pages/Exchange/components/GasFeePanel/JupiterRouteDetails'
import { IDetailedRowsProps } from '@src/pages/Exchange/components/GasFeePanel/types'
import { isFeatureEnabled } from '@src/utils/environment'
import { formatExchangePrice } from '@src/utils/formatExchangePrice'
import { formatNumber } from '@src/utils/formatFee'
import { truncateAddress } from '@src/utils/truncateAddress'

export function DetailedRows({
    destinationAddress,
    evmWalletAddress,
    sellData,
    buyData,
    gasFeeString,
    liquiditySource,
    machFee,
    miles,
    sandDollars,
}: IDetailedRowsProps) {
    const isSandDollarsEnabled = isFeatureEnabled('SAND_DOLLARS')
    return (
        <div className='flex flex-col gap-4 text-fluid-sm'>
            <div className='flex items-center justify-between pt-2'>
                <span className='text-fluid-sm font-medium text-mach-sub-text'>Receive to</span>
                {destinationAddress ? (
                    <div className='flex items-center gap-[6px] rounded-md bg-mach-gray px-2 py-2'>
                        <span className='text-fluid-sm text-white'>{truncateAddress(destinationAddress)}</span>
                        <CopyToClipboard anchorId='dest-address-1' textToCopy={destinationAddress} />
                    </div>
                ) : (
                    <span className='rounded-md bg-mach-gray px-2 py-2 text-fluid-sm text-white'>
                        No destination address specified
                    </span>
                )}
            </div>

            <div className='flex items-center justify-between'>
                <span className='text-fluid-sm font-medium text-mach-sub-text'>Rate</span>
                <span className='flex items-center rounded-md bg-mach-gray px-2 py-2 text-fluid-sm text-white'>
                    1&nbsp;
                    <span className='max-w-[80px] truncate'>{sellData.token?.symbol}</span>
                    &nbsp;=&nbsp;
                    {formatExchangePrice(sellData, buyData)}&nbsp;
                    {<span className='max-w-[80px] truncate'>{buyData.token?.symbol}</span>}
                </span>
            </div>

            <div className='flex items-center justify-between'>
                <span className='text-fluid-sm font-medium text-mach-sub-text'>Gas fees</span>
                <div className='flex items-center gap-1 rounded-md bg-mach-gray px-2 py-2'>
                    <FuelIcon />
                    <span className='text-fluid-sm text-white'>{gasFeeString}</span>
                </div>
            </div>

            <ChainSpecificGasDetails sellData={sellData} />

            <div className='flex items-center justify-between'>
                <span className='text-fluid-sm font-medium text-mach-sub-text'>Mach fee (0.01%)</span>
                {liquiditySource === LiquiditySource.cctpDirect ? (
                    <span className='rounded-md bg-mach-gray px-2 py-2 text-white'>
                        <span className='w-fit items-center rounded-md bg-mach-green px-2 py-0.5 text-fluid-xs text-white'>
                            FREE
                        </span>
                    </span>
                ) : (
                    <span className='rounded-md bg-mach-gray px-2 py-2 text-white'>
                        {+formatNumber(machFee) < 0.01 ? '<$0.01' : `$${formatNumber(machFee)}`}
                    </span>
                )}
            </div>

            <div className='flex items-center justify-between'>
                <span className='text-fluid-sm text-mach-sub-text'>Miles earned</span>
                <span className='flex items-center gap-1 rounded-md bg-mach-gray px-2 py-2'>
                    <span className='text-fluid-sm text-white'>
                        {miles.toLocaleString('en-us', { maximumFractionDigits: 3 })}
                    </span>
                    <span className='text-fluid-sm text-white'>Miles</span>
                </span>
            </div>

            {/* Show Sand Dollars earned row if enabled and amount > 0 */}
            {isSandDollarsEnabled && sandDollars && sandDollars > 0 && (
                <div className='flex items-center justify-between'>
                    <span className='text-fluid-sm text-mach-sub-text'>Sand Dollars earned</span>
                    <span className='flex items-center gap-1 rounded-md bg-mach-gray px-2 py-2'>
                        <span className='text-fluid-sm text-white'>
                            {sandDollars.toLocaleString('en-us', { maximumFractionDigits: 6 })}
                        </span>
                        <span className='text-fluid-sm text-white'>SD</span>
                    </span>
                </div>
            )}

            {/* Show Jupiter route details for Jupiter swaps */}
            {liquiditySource === LiquiditySource.jupiter && <JupiterRouteDetails />}
        </div>
    )
}
